import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from "luxon";
import AvantageGroupeRabais from '../components/AvantageGroupeRabais';
import {
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import HomeIcon           from '@mui/icons-material/Home';
import NavigateNextIcon   from '@mui/icons-material/NavigateNext';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import CalendarTodayIcon  from '@mui/icons-material/CalendarToday';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import AttachMoneyIcon    from '@mui/icons-material/AttachMoney';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import PeopleIcon         from '@mui/icons-material/People';
import StarIcon           from '@mui/icons-material/Star';
import PhoneIcon          from '@mui/icons-material/Phone';
import SchoolIcon         from '@mui/icons-material/School';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupsIcon        from '@mui/icons-material/Groups';

// Configuration globale de Luxon pour utiliser UTC
import { Settings } from "luxon";
Settings.defaultZone = "utc";

// ─── Helpers ───────────────────────────────────────────────────────────────────
const getImageSrc = (img) => {
  if (!img) return '';
  if (img.startsWith('http') || img.startsWith('//')) return img;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
  return img.startsWith('/') ? `${base}${img}` : `${base}/${img}`;
};

const MODE_META = {
  presentiel:    { label: 'Présentiel',                  color: '#2e7d32' },
  en_ligne:      { label: 'Visioconférence',             color: '#1565c0' },
  auto_formation:{ label: 'En ligne (SANS CERTIFICATION)',color: '#6a1b9a' },
  mixte:         { label: 'Mixte',                       color: '#e65100' },
};
const getModeMeta = (m) => MODE_META[m] || { label: m, color: '#555' };

// ─── Styled ────────────────────────────────────────────────────────────────────
const PageWrap = styled(Box)(({ theme }) => ({
  backgroundColor: '#f7f8fc',
  minHeight: '100vh',
  paddingBottom: theme.spacing(8),
}));

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '1160px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const InfoSection = styled(Paper)(({ theme }) => ({
  borderRadius: '20px',
  border: '1px solid rgba(0,0,0,0.07)',
  boxShadow: '0 6px 24px rgba(0,0,0,0.06)',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
}));

const SectionHead = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const SessionCard = styled(Paper)(({ theme, available }) => ({
  padding: theme.spacing(2, 2.5),
  marginBottom: theme.spacing(1.5),
  borderRadius: '14px',
  border: available === 'true' ? '1px solid rgba(46,125,50,0.25)' : '1px solid rgba(211,47,47,0.2)',
  backgroundColor: available === 'true' ? 'rgba(46,125,50,0.02)' : 'rgba(211,47,47,0.02)',
  transition: 'box-shadow 0.2s, transform 0.2s',
  '&:last-child': { marginBottom: 0 },
  '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
}));

// ─── Composant ────────────────────────────────────────────────────────────────
const FormationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formation, setFormation]           = useState(null);
  const [sessions, setSessions]             = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [sortedSessions, setSortedSessions] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [error, setError]                   = useState(null);
  const [sortBy]                            = useState('date');
  const [cityFilter, setCityFilter]         = useState('');
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');
  const [availableCities, setAvailableCities] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // ── Helpers logique (tous inchangés) ────────────────────────────────────────
  const isSessionExpired = (session) => {
    if (!session.date_fin) return false;
    try {
      return DateTime.fromISO(session.date_fin.split('T')[0]).startOf('day') < DateTime.now().startOf('day');
    } catch { return false; }
  };

  const parseArray = (val) => {
    if (Array.isArray(val)) return val;
    if (val == null) return [];
    if (typeof val === 'string') {
      if (val.trim() === '') return [];
      try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
    }
    return [];
  };

  const formatDateForDisplay = (ds) => {
    if (!ds) return '';
    try { return DateTime.fromISO(ds.split('T')[0]).toFormat('dd/MM/yyyy'); } catch { return ds; }
  };

  const formatDateRange = (s, e) => {
    const start = formatDateForDisplay(s);
    const end   = formatDateForDisplay(e);
    if (!end || s === e) return start;
    return `Du ${start} au ${end}`;
  };

  const compareDates = (a, b) => {
    try { return DateTime.fromISO(a.split('T')[0]).toMillis() - DateTime.fromISO(b.split('T')[0]).toMillis(); }
    catch { return 0; }
  };

  const formatTime = (t) => t ? t.substring(0, 5) : '';

  const sortSessions = (list, method) => {
    if (!list?.length) return [];
    const sorted = [...list];
    if (method === 'date')  return sorted.sort((a, b) => compareDates(a.date_debut, b.date_debut));
    if (method === 'ville') return sorted.sort((a, b) => a.ville.toLowerCase().localeCompare(b.ville.toLowerCase()) || compareDates(a.date_debut, b.date_debut));
    return sorted;
  };

  const handleIndividualRegistration = (session) => {
    navigate('/inscription-individuelle', {
      state: {
        formationId: id,
        sessionId: session.id,
        formationTitre: formation?.titre,
        sessionInfo: { ville: session.ville, date_debut: session.date_debut, date_fin: session.date_fin, heure_debut: session.heure_debut, heure_fin: session.heure_fin, adresse: session.adresse },
      },
    });
  };

  const handleGroupRegistration = () => {
    navigate('/inscription-groupe', {
      state: {
        formationId: id,
        formationTitre: formation?.titre,
      },
    });
  };

  const fetchSessions = async (params = {}) => {
    try {
      setLoadingSessions(true);
      const q = new URLSearchParams({ formation_id: id, is_active: 'true', ...params }).toString();
      const res = await axios.get(`${API_URL}/sessions?${q}`);
      if (res.data.success) {
        const normalized = (res.data.data || []).map(s => ({
          ...s,
          date_debut: s.date_debut || s.date,
          date_fin:   s.date_fin   || s.date,
          heure_debut: s.heure_debut || (s.heure ? s.heure.split(' à ')[0] : ''),
          heure_fin:   s.heure_fin   || (s.heure ? s.heure.split(' à ')[1] : ''),
        }));
        const active = normalized.filter(s => !isSessionExpired(s) && !s.is_private);
        setSessions(active);
        setFilteredSessions(active);
        setSortedSessions(sortSessions(active, sortBy));
        setAvailableCities(Array.from(new Set(active.map(s => s.ville).filter(Boolean))).sort());
      }
    } catch (err) {
      console.error('Erreur sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/formations/${id}`);
        if (res.data.success) {
          setFormation({
            ...res.data.data,
            competences_enseignees: parseArray(res.data.data.competences_enseignees),
            public_cible: parseArray(res.data.data.public_cible),
            avantages: parseArray(res.data.data.avantages),
          });
          await fetchSessions();
        } else { setError('Formation non trouvée'); }
      } catch { setError('Impossible de charger les détails'); }
      finally  { setLoading(false); }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const params = {};
    if (startDate) params.date_debut = startDate;
    if (endDate)   params.date_fin   = endDate;
    if (cityFilter) params.ville     = cityFilter;
    fetchSessions(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, startDate, endDate, cityFilter]);

  useEffect(() => {
    if (filteredSessions.length > 0) setSortedSessions(sortSessions(filteredSessions, sortBy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, filteredSessions]);

  // ── Loading / Error ──────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ textAlign: 'center', py: 12 }}>
      <CircularProgress sx={{ color: '#c62828', mb: 2 }} />
      <Typography variant="body2" sx={{ color: '#888' }}>Chargement de la formation…</Typography>
    </Box>
  );

  if (error || !formation) return (
    <Wrap sx={{ py: 6 }}>
      <Alert severity="error" sx={{ mb: 3, borderRadius: '14px' }}>{error || 'Formation non trouvée'}</Alert>
      <Button component={Link} to="/formations" startIcon={<ArrowBackIcon />} variant="outlined"
        sx={{ borderColor: '#c62828', color: '#c62828', borderRadius: '50px' }}>
        Retour aux formations
      </Button>
    </Wrap>
  );

  const competences = Array.isArray(formation.competences_enseignees) ? formation.competences_enseignees : [];
  const publicCible = Array.isArray(formation.public_cible) ? formation.public_cible : [];
  const avantages   = Array.isArray(formation.avantages) ? formation.avantages : [];
  const imageUrl    = formation.image ? getImageSrc(formation.image) : '';
  const modeMeta    = getModeMeta(formation.mode);

  return (
    <PageWrap>

      {/* ══════════════ EN-TÊTE SOBRE (blanc, pas de rouge) ══════════════ */}
      <Box sx={{ backgroundColor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)', pt: 3, pb: 4 }}>
        <Wrap>
          {/* Breadcrumb */}
          <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: '0.8rem' }} />} sx={{ mb: 2.5 }}>
            <MuiLink component={Link} to="/" sx={{ color: '#888', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 0.4, textDecoration: 'none', '&:hover': { color: '#c62828' } }}>
              <HomeIcon sx={{ fontSize: '0.85rem' }} /> Accueil
            </MuiLink>
            <MuiLink component={Link} to="/formations" sx={{ color: '#888', fontSize: '0.78rem', textDecoration: 'none', '&:hover': { color: '#c62828' } }}>
              Formations
            </MuiLink>
            <Typography sx={{ color: '#333', fontSize: '0.78rem', fontWeight: 600 }}>
              {formation.titre?.substring(0, 40)}{formation.titre?.length > 40 ? '…' : ''}
            </Typography>
          </Breadcrumbs>

          {/* Catégorie + titre */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            {formation.categorie && (
              <Box sx={{
                background: `${formation.categorie.couleur || '#c62828'}15`,
                color: formation.categorie.couleur || '#c62828',
                border: `1px solid ${formation.categorie.couleur || '#c62828'}33`,
                borderRadius: '20px', px: 1.5, py: 0.3,
                fontSize: '0.75rem', fontWeight: 700,
              }}>
                {formation.categorie.nom}
              </Box>
            )}
            <Box sx={{ background: `${modeMeta.color}12`, color: modeMeta.color, border: `1px solid ${modeMeta.color}30`, borderRadius: '20px', px: 1.5, py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>
              {modeMeta.label}
            </Box>
          </Box>

          <Typography variant="h1" sx={{ fontSize: { xs: '1.7rem', md: '2.4rem' }, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.2, mb: 1 }}>
            {formation.titre}
          </Typography>
          {formation.sous_titre && (
            <Typography sx={{ fontSize: '1rem', color: '#666', mb: 2 }}>{formation.sous_titre}</Typography>
          )}

          {/* Chips durée + tarif */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '20px', px: 1.75, py: 0.6, fontSize: '0.82rem', fontWeight: 700, color: '#333' }}>
              <AccessTimeIcon sx={{ fontSize: '0.9rem', color: '#666' }} /> {formation.heure}h de formation
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '20px', px: 1.75, py: 0.6, fontSize: '0.82rem', fontWeight: 700, color: '#333' }}>
              <AttachMoneyIcon sx={{ fontSize: '0.9rem', color: '#666' }} /> {formation.tarif} $ / personne
            </Box>
          </Stack>
        </Wrap>
      </Box>

      {/* ══════════════ CORPS PRINCIPAL ══════════════ */}
      <Wrap sx={{ mt: 3.5 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: 'flex-start' }}>

          {/* ── Colonne gauche : image + description ── */}
          <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>

            {/* Image en rectangle */}
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={formation.titre}
                sx={{
                  width: '100%',
                  height: { xs: 220, sm: 300, md: 340 },
                  objectFit: 'cover',
                  borderRadius: '16px',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
                  display: 'block',
                  mb: 3,
                }}
              />
            ) : (
              <Box sx={{ width: '100%', height: 220, borderRadius: '16px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <SchoolIcon sx={{ fontSize: 60, color: '#ccc' }} />
              </Box>
            )}

            {/* Description */}
            <InfoSection elevation={0}>
              <SectionHead>
                <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>
                  <SchoolIcon sx={{ fontSize: '1.1rem' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.97rem', color: '#1a1a1a' }}>Description</Typography>
              </SectionHead>
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ color: '#4a4a4a', lineHeight: 1.9, fontSize: '0.96rem' }}>
                  {formation.description}
                </Typography>
              </Box>
            </InfoSection>

            {/* Compétences */}
            {competences.length > 0 && (
              <InfoSection elevation={0}>
                <SectionHead>
                  <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: 'rgba(46,125,50,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2e7d32' }}>
                    <CheckCircleIcon sx={{ fontSize: '1.1rem' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.97rem', color: '#1a1a1a' }}>Compétences enseignées</Typography>
                </SectionHead>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {competences.map((c, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, borderRadius: '9px', background: 'rgba(46,125,50,0.04)', border: '1px solid rgba(46,125,50,0.1)' }}>
                        <CheckCircleIcon sx={{ fontSize: '0.95rem', color: '#2e7d32', flexShrink: 0, mt: 0.1 }} />
                        <Typography variant="body2" sx={{ color: '#444', fontSize: '0.86rem', lineHeight: 1.5 }}>{c}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </InfoSection>
            )}

            {/* Public cible + Avantages */}
            {(publicCible.length > 0 || avantages.length > 0) && (
              <InfoSection elevation={0}>
                {publicCible.length > 0 && (
                  <>
                    <SectionHead>
                      <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: 'rgba(21,101,192,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1565c0' }}>
                        <PeopleIcon sx={{ fontSize: '1.1rem' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.97rem', color: '#1a1a1a' }}>Public cible</Typography>
                    </SectionHead>
                    <Box sx={{ p: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {publicCible.map((p, i) => (
                        <Box key={i} sx={{ background: 'rgba(21,101,192,0.07)', color: '#1565c0', border: '1px solid rgba(21,101,192,0.18)', borderRadius: '20px', px: 1.5, py: 0.4, fontSize: '0.8rem', fontWeight: 600 }}>
                          {p}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                {publicCible.length > 0 && avantages.length > 0 && <Divider />}
                {avantages.length > 0 && (
                  <>
                    <SectionHead>
                      <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
                        <StarIcon sx={{ fontSize: '1.1rem' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.97rem', color: '#1a1a1a' }}>Avantages</Typography>
                    </SectionHead>
                    <Box sx={{ p: 3 }}>
                      <Stack spacing={1.25}>
                        {avantages.map((av, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            <StarIcon sx={{ fontSize: '0.9rem', color: '#d97706', flexShrink: 0, mt: 0.2 }} />
                            <Typography variant="body2" sx={{ color: '#4a4a4a', lineHeight: 1.7 }}>{av}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
              </InfoSection>
            )}

            {/* Note auto-formation */}
            {formation.mode === 'auto_formation' && (
              <Alert severity="info" sx={{ borderRadius: '14px', mt: 1 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Formation autonome</Typography>
                <ul style={{ margin: '0 0 0 16px', padding: 0, color: '#555', fontSize: '0.88rem', lineHeight: 1.8 }}>
                  <li>La date affichée n'est pas à considérer.</li>
                  <li>La formation est accessible en tout temps selon votre horaire.</li>
                  <li>Un certificat de réussite téléchargeable sera délivré à la fin du cours.</li>
                </ul>
              </Alert>
            )}
          </Box>

          {/* ── Colonne droite : sessions ── */}
          <Box sx={{ width: { xs: '100%', lg: '390px' }, flexShrink: 0 }}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 20 } }}>

              <InfoSection elevation={0}>
                {/* En-tête neutre (gris foncé, pas rouge) */}
                <Box sx={{ background: '#1e293b', p: 2.5, color: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <EventAvailableIcon sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem' }}>Sessions disponibles</Typography>
                  </Box>
                  {/* Résumé prix + durée */}
                  <Stack direction="row" spacing={1.5}>
                    <Box sx={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', px: 2, py: 1, flex: 1, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, lineHeight: 1 }}>{formation.tarif} $</Typography>
                      <Typography sx={{ fontSize: '0.68rem', opacity: 0.7, mt: 0.25 }}>par personne</Typography>
                    </Box>
                    <Box sx={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', px: 2, py: 1, flex: 1, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, lineHeight: 1 }}>{formation.heure}h</Typography>
                      <Typography sx={{ fontSize: '0.68rem', opacity: 0.7, mt: 0.25 }}>de formation</Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  {/* Filtres */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', whiteSpace: 'nowrap' }}>Du</Typography>
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                        style={{ flex: 1, padding: '6px 8px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '0.78rem', fontFamily: 'inherit', backgroundColor: '#fafafa' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', whiteSpace: 'nowrap' }}>Au</Typography>
                      <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                        style={{ flex: 1, padding: '6px 8px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '0.78rem', fontFamily: 'inherit', backgroundColor: '#fafafa' }} />
                    </Box>
                    {availableCities.length > 1 && (
                      <select value={cityFilter} onChange={e => setCityFilter(e.target.value)}
                        style={{ flex: 1, minWidth: 0, padding: '6px 8px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '0.78rem', fontFamily: 'inherit', backgroundColor: '#fafafa' }}>
                        <option value="">Toutes les villes</option>
                        {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    )}
                    {(startDate || endDate || cityFilter) && (
                      <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); setCityFilter(''); }}
                        sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', p: '4px 10px', borderRadius: '20px', border: '1px solid #e0e0e0', '&:hover': { background: '#f5f5f5' } }}>
                        Réinitialiser
                      </Button>
                    )}
                  </Box>

                  {/* Liste sessions */}
                  {loadingSessions ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ color: '#555' }} />
                    </Box>
                  ) : sortedSessions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#bbb' }}>
                      <EventAvailableIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#999' }}>
                        {sessions.length === 0 ? 'Aucune session à venir' : 'Aucune session ne correspond aux filtres'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{
                      maxHeight: 420, overflowY: 'auto', pr: 0.5,
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: '#ddd', borderRadius: '2px' },
                    }}>
                      {sortedSessions.map(session => {
                        const available = session.place_disponible > 0 && session.allow_registration;
                        return (
                          <SessionCard key={session.id} available={available ? 'true' : 'false'}>
                            {/* Lieu */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                              <LocationOnIcon sx={{ fontSize: '0.85rem', color: '#999' }} />
                              <Typography variant="caption" sx={{ color: '#777', fontSize: '0.74rem' }}>
                                {session.ville} — {session.adresse}
                              </Typography>
                            </Box>
                            {/* Date / heure */}
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: '#555' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.87rem' }}>
                                  {formatDateRange(session.date_debut, session.date_fin)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: '0.9rem', color: '#555' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', fontSize: '0.84rem' }}>
                                  {formatTime(session.heure_debut)} – {formatTime(session.heure_fin)}
                                </Typography>
                              </Box>
                            </Box>
                            {/* Bouton */}
                            <Button
                              fullWidth size="small"
                              variant={available ? 'contained' : 'outlined'}
                              disabled={!available}
                              onClick={() => available && handleIndividualRegistration(session)}
                              sx={{
                                fontWeight: 700, borderRadius: '10px', textTransform: 'none', fontSize: '0.85rem', py: 0.85,
                                background: available ? 'linear-gradient(135deg, #c62828, #8e0000)' : 'transparent',
                                borderColor: available ? 'transparent' : '#e0e0e0',
                                color: available ? '#fff' : '#bbb',
                                '&:hover': available ? { background: 'linear-gradient(135deg, #b71c1c, #7f0000)' } : {},
                                '&.Mui-disabled': { background: 'transparent', borderColor: '#eee', color: '#ccc' },
                              }}
                            >
                              {!session.allow_registration ? 'Inscriptions fermées' : session.place_disponible === 0 ? 'Complet' : "S'inscrire"}
                            </Button>
                          </SessionCard>
                        );
                      })}
                    </Box>
                  )}

                  <Divider sx={{ my: 2.5 }} />

                  {/* ── Inscription de groupe ── */}
                  <Box sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: '14px',
                    background: 'rgba(198,40,40,0.04)',
                    border: '1px solid rgba(198,40,40,0.2)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <GroupsIcon sx={{ color: '#c62828', fontSize: '1.3rem' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#c62828', fontSize: '0.9rem' }}>
                        Inscription de groupe
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#555', fontSize: '0.82rem', lineHeight: 1.6, mb: 1.75 }}>
                      Vous représentez une entreprise, une école ou un organisme ? Inscrivez votre groupe et notre équipe vous contactera pour organiser une session adaptée.
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GroupsIcon />}
                      onClick={handleGroupRegistration}
                      sx={{
                        borderColor: '#c62828',
                        color: '#c62828',
                        fontWeight: 700,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        '&:hover': {
                          background: 'rgba(198,40,40,0.06)',
                          borderColor: '#8e0000',
                        },
                      }}
                    >
                      S'inscrire en groupe
                    </Button>
                    <AvantageGroupeRabais sx={{ justifyContent: 'flex-start', textAlign: 'left', mt: 1.5 }} />
                  </Box>

                  {/* Aucune date convenable */}
                  <Box sx={{ textAlign: 'center', mb: 2, p: 2, background: '#f9f9f9', borderRadius: '12px' }}>
                    <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic', lineHeight: 1.7, mb: 1.25, fontSize: '0.84rem' }}>
                      Aucune date ne vous convient ? Contactez-nous et nous trouverons un moment adapté.
                    </Typography>
                    <Button size="small" onClick={() => navigate('/contact', { state: { formationId: id } })}
                      sx={{ color: '#c62828', fontWeight: 700, fontSize: '0.8rem', textTransform: 'none', border: '1px solid rgba(198,40,40,0.22)', borderRadius: '20px', px: 2, '&:hover': { background: 'rgba(198,40,40,0.05)' } }}>
                      Contactez-nous
                    </Button>
                  </Box>

                  <Stack spacing={1.5}>
                    <Button fullWidth variant="contained"
                      onClick={() => navigate('/contact', { state: { formationId: id } })}
                      startIcon={<PhoneIcon />}
                      sx={{ background: 'linear-gradient(135deg, #c62828, #8e0000)', fontWeight: 700, py: 1.3, borderRadius: '50px', textTransform: 'none', '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)' } }}>
                      Demander des informations
                    </Button>
                    <Button component={Link} to="/formations" fullWidth variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      sx={{ borderColor: '#e0e0e0', color: '#666', fontWeight: 600, py: 1.1, borderRadius: '50px', textTransform: 'none', '&:hover': { borderColor: '#555', color: '#333', background: '#f5f5f5' } }}>
                      Toutes les formations
                    </Button>
                  </Stack>
                </Box>
              </InfoSection>

        

            </Box>
          </Box>
        </Box>
      </Wrap>
    </PageWrap>
  );
};

export default FormationDetail;
